import { useState } from "react";
import { FaTimes, FaStar } from "react-icons/fa";
import api from '../api/axios'; // adjust to match your existing api client import path

const COLORS = {
  blue: "#2563EB",
  orange: "#F59E0B",
  dark: "#0D1B3D",
};

const RATING_FIELDS = [
  { key: "technical_rating", label: "Technical" },
  { key: "communication_rating", label: "Communication" },
  { key: "aptitude_rating", label: "Aptitude" },
  { key: "culture_fit_rating", label: "Culture Fit" },
];

const STATUS_OPTIONS = ["Scheduled", "Completed", "Rescheduled", "Cancelled"];
const RECOMMENDATION_OPTIONS = [
  { value: "proceed", label: "Proceed" },
  { value: "hold", label: "Hold" },
  { value: "reject", label: "Reject" },
];

function RatingInput({ value, onChange }) {
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
        <button
          type="button"
          key={n}
          onClick={() => onChange(n)}
          className="text-lg leading-none"
          style={{ color: value >= n ? COLORS.orange : "#E5E7EB" }}
          aria-label={`Rate ${n}`}
        >
          <FaStar />
        </button>
      ))}
      <span className="ml-2 text-sm text-gray-500">{value ? `${value}/10` : "—"}</span>
    </div>
  );
}

export default function InterviewFeedbackModal({ interview, onClose, onSaved }) {
  const [status, setStatus] = useState(interview.status || "Scheduled");
  const [ratings, setRatings] = useState({
    technical_rating: interview.technical_rating || 0,
    communication_rating: interview.communication_rating || 0,
    aptitude_rating: interview.aptitude_rating || 0,
    culture_fit_rating: interview.culture_fit_rating || 0,
  });
  const [recommendation, setRecommendation] = useState(interview.recommendation || "");
  const [comments, setComments] = useState(interview.comments || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      if (status !== interview.status) {
        await api.put(`/employer/interviews/${interview.id}/status`, { status });
      }
      await api.put(`/employer/interviews/${interview.id}/feedback`, {
        ...ratings,
        recommendation: recommendation || null,
        comments: comments || null,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Couldn't save feedback.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div
          className="flex items-center justify-between rounded-t-xl px-6 py-4"
          style={{ backgroundColor: COLORS.dark }}
        >
          <div>
            <h2 className="text-lg font-semibold text-white">{interview.candidate_name}</h2>
            <p className="text-sm text-white/70">
              {interview.job_title || "General"} · {interview.type}
            </p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white">
            <FaTimes />
          </button>
        </div>

        <div className="max-h-[70vh] space-y-5 overflow-y-auto px-6 py-5">
          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium" style={{ color: COLORS.dark }}>
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-3">
            {RATING_FIELDS.map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm font-medium" style={{ color: COLORS.dark }}>
                  {label}
                </span>
                <RatingInput
                  value={ratings[key]}
                  onChange={(v) => setRatings((r) => ({ ...r, [key]: v }))}
                />
              </div>
            ))}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium" style={{ color: COLORS.dark }}>
              Recommendation
            </label>
            <div className="flex gap-2">
              {RECOMMENDATION_OPTIONS.map((opt) => (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => setRecommendation(opt.value)}
                  className={`flex-1 rounded-md border px-3 py-2 text-sm ${
                    recommendation === opt.value
                      ? "border-transparent text-white"
                      : "border-gray-300 text-gray-600"
                  }`}
                  style={
                    recommendation === opt.value
                      ? {
                          backgroundColor:
                            opt.value === "reject"
                              ? "#DC2626"
                              : opt.value === "hold"
                              ? COLORS.orange
                              : COLORS.blue,
                        }
                      : {}
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium" style={{ color: COLORS.dark }}>
              Comments
            </label>
            <textarea
              rows={3}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2"
              placeholder="Notes for the hiring team..."
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
            style={{ backgroundColor: COLORS.blue }}
          >
            {saving ? "Saving..." : "Save Feedback"}
          </button>
        </div>
      </div>
    </div>
  );
}